import React, { ComponentType, useCallback } from 'react';
import { useApplicationInsights } from '../hooks/useApplicationInsights';

// Re-export Microsoft's withAITracking for simplicity
export { withAITracking } from '@microsoft/applicationinsights-react-js';

/**
 * React Error Boundary that logs exceptions to Application Insights
 */
interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
}

interface AIErrorBoundaryProps {
    children: React.ReactNode;
    fallback?: React.ComponentType<{ error?: Error; resetErrorBoundary: () => void }>;
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

class AIErrorBoundary extends React.Component<AIErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: AIErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Log to console for debugging
        console.error('[AI] React Error Boundary caught error:', error, errorInfo);

        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    resetErrorBoundary = () => {
        this.setState({ hasError: false, error: undefined });
    }

    override render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                const Fallback = this.props.fallback;
                return <Fallback error={this.state.error} resetErrorBoundary={this.resetErrorBoundary} />;
            }

            return (
                <div style={{ padding: '20px', border: '1px solid red', margin: '10px' }}>
                    <h2>Something went wrong.</h2>
                    <details>
                        <summary>Error Details</summary>
                        <pre>{this.state.error?.toString()}</pre>
                    </details>
                    <button onClick={this.resetErrorBoundary}>Try again</button>
                </div>
            );
        }

        return this.props.children;
    }
}

/**
 * Enhanced Error Boundary that integrates with Application Insights
 * This wrapper uses hooks to access the AI context and pass error handling to the class boundary
 */
const AIErrorBoundaryWithTracking: React.FC<AIErrorBoundaryProps> = (props) => {
    const { trackException } = useApplicationInsights();

    const handleError = useCallback((error: Error, errorInfo: React.ErrorInfo) => {
        // Track exception in Application Insights
        trackException(error, {
            componentStack: errorInfo.componentStack,
            digest: (errorInfo as any).digest, // React 18+ error digest
            timestamp: new Date().toISOString(),
            errorBoundary: 'AIErrorBoundaryWithTracking'
        });

        // Call original onError if provided
        props.onError?.(error, errorInfo);
    }, [trackException, props.onError]);

    return <AIErrorBoundary {...props} onError={handleError} />;
};

/**
 * Convenience HOC to wrap components with AI-enabled error boundary
 */
export function withAIErrorBoundary<P extends object>(
    WrappedComponent: ComponentType<P>,
    fallback?: React.ComponentType<{ error?: Error; resetErrorBoundary: () => void }>
): ComponentType<P> {
    const ComponentWithErrorBoundary: React.FC<P> = (props) => {
        return (
            <AIErrorBoundaryWithTracking fallback={fallback}>
                <WrappedComponent {...props} />
            </AIErrorBoundaryWithTracking>
        );
    };

    ComponentWithErrorBoundary.displayName = `withAIErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

    return ComponentWithErrorBoundary;
}
